import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Mail, Phone, ExternalLink, Building2,
  MapPin, Pencil, Send, Clock, DollarSign, Trash2,
  FileText, FilePlus, Copy, Check, ShieldCheck, AlertTriangle,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { PlatformRegion, PlatformDocumentTemplate, PlatformDocumentInstance } from "@shared/schema";

const STATUS_OPTIONS = [
  { value: "trial", label: "On Trial", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  { value: "active", label: "Active", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  { value: "paused", label: "Paused", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  { value: "past_due", label: "Past Due", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
  { value: "churned", label: "Cancelled", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
];

type Customer = {
  id: string; recordId?: string | null; companyName: string; contactName?: string | null;
  phoneNumber?: string | null; emailAddress?: string | null; address?: string | null;
  city?: string | null; state?: string | null; zipCode?: string | null;
  website?: string | null; businessType?: string | null; region?: string | null;
  rep?: string | null; status: string; planTier?: string | null;
  baseRateCents: number; discountPercent: string; maxRepDiscountPercent: string;
  mrrCents: number; notes?: string | null; emailOptedOut: boolean;
  requiresDocument: boolean;
  lastContacted?: string | null; workspaceSlug?: string | null;
  workspaceId: string; prospectId?: string | null;
  createdAt: string; updatedAt: string;
};

type Activity = {
  id: string; customerId: string; type: "call" | "email" | "note";
  notes?: string | null; createdAt: string;
};

interface CustomerRecordDialogProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: (c: Customer) => void;
}

function statusBadge(status: string) {
  const opt = STATUS_OPTIONS.find(s => s.value === status);
  return opt ? (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${opt.color}`}>
      {opt.label}
    </span>
  ) : <span className="text-xs text-muted-foreground">{status}</span>;
}

export function CustomerRecordDialog({ customer, isOpen, onClose, onUpdated }: CustomerRecordDialogProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [tab, setTab] = useState<"details" | "activity" | "documents">("details");
  const [copied, setCopied] = useState<string | null>(null);
  const [sendingDocId, setSendingDocId] = useState<string | null>(null);
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [newDocName, setNewDocName] = useState("");
  const [newDocTemplateId, setNewDocTemplateId] = useState<string>("none");
  const [newDocBody, setNewDocBody] = useState("");
  const [newDocRecipientName, setNewDocRecipientName] = useState("");
  const [newDocRecipientEmail, setNewDocRecipientEmail] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<Partial<Customer>>({});
  const [logType, setLogType] = useState<"call" | "email" | "note">("call");
  const [logNotes, setLogNotes] = useState("");
  const [isLogging, setIsLogging] = useState(false);

  const { data: regions = [] } = useQuery<PlatformRegion[]>({
    queryKey: ["/api/platform/regions"],
  });

  const { data: platformBizTypes = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/platform/business-types"],
  });
  const businessTypes = platformBizTypes.map((t) => t.name);

  const { data: docTemplates = [] } = useQuery<PlatformDocumentTemplate[]>({
    queryKey: ["/api/platform/document-templates"],
    enabled: tab === "documents" || newDocOpen,
  });

  const { data: documents = [], refetch: refetchDocs } = useQuery<PlatformDocumentInstance[]>({
    queryKey: ["/api/platform/customers", customer?.id, "documents"],
    queryFn: async () => {
      if (!customer?.id) return [];
      const r = await fetch(`/api/platform/customers/${customer.id}/documents`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!customer?.id && tab === "documents",
  });

  const { data: activities = [], refetch: refetchActivities } = useQuery<Activity[]>({
    queryKey: ["/api/platform/customers", customer?.id, "activities"],
    queryFn: async () => {
      if (!customer?.id) return [];
      const r = await fetch(`/api/platform/customers/${customer.id}/activities`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!customer?.id && tab === "activity",
  });

  useEffect(() => {
    if (customer) {
      setForm({ ...customer });
      setTab("details");
      setEditMode(false);
    }
  }, [customer?.id]);

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<Customer>) =>
      apiRequest("PATCH", `/api/platform/customers/${customer!.id}`, updates),
    onSuccess: async (res) => {
      const updated = await res.json();
      qc.invalidateQueries({ queryKey: ["/api/platform/customers"] });
      onUpdated?.(updated);
      setEditMode(false);
      toast({ title: "Customer saved" });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const handleSave = () => {
    if (!customer) return;
    const updates: Partial<Customer> = {};
    const fields: (keyof Customer)[] = [
      "companyName", "contactName", "phoneNumber", "emailAddress",
      "address", "city", "state", "zipCode", "website",
      "businessType", "region", "rep", "status", "planTier",
      "baseRateCents", "discountPercent", "notes", "emailOptedOut", "requiresDocument",
    ];
    for (const f of fields) {
      if ((form as any)[f] !== (customer as any)[f]) {
        (updates as any)[f] = (form as any)[f];
      }
    }
    if (Object.keys(updates).length > 0) updateMutation.mutate(updates);
    else setEditMode(false);
  };

  const handleLogActivity = async () => {
    if (!customer || !logNotes.trim()) return;
    setIsLogging(true);
    try {
      await apiRequest("POST", `/api/platform/customers/${customer.id}/activities`, {
        type: logType,
        notes: logNotes.trim(),
      });
      setLogNotes("");
      refetchActivities();
      qc.invalidateQueries({ queryKey: ["/api/platform/customers"] });
      toast({ title: "Activity logged" });
    } catch {
      toast({ title: "Failed to log activity", variant: "destructive" });
    } finally {
      setIsLogging(false);
    }
  };

  const mrrDisplay = customer ? `$${((customer.mrrCents || 0) / 100).toFixed(2)}/mo` : "";
  const baseDisplay = form.baseRateCents !== undefined ? `$${(form.baseRateCents / 100).toFixed(2)}` : "";

  if (!customer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { setEditMode(false); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl truncate">{customer.companyName}</DialogTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {customer.recordId && (
                  <span className="text-xs text-muted-foreground font-mono">{customer.recordId}</span>
                )}
                {statusBadge(customer.status)}
                {customer.planTier && (
                  <Badge variant="outline" className="text-xs capitalize">{customer.planTier}</Badge>
                )}
                {customer.workspaceSlug && (
                  <a
                    href={`/w/${customer.workspaceSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" /> Workspace
                  </a>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {editMode ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => { setForm({ ...customer }); setEditMode(false); }}>Cancel</Button>
                  <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-save-customer">
                    {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditMode(true)} data-testid="button-edit-customer">
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </div>
          </div>

          {/* Tab strip */}
          <div className="flex gap-0 border-b mt-4">
            {([
              { key: "details", label: "Details" },
              { key: "activity", label: "Activity" },
              { key: "documents", label: "Documents" },
            ] as const).map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-customer-${t.key}`}
              >
                {t.label}
                {t.key === "documents" && customer.requiresDocument && documents.filter(d => d.status !== "completed" && d.status !== "voided").length === 0 && (
                  <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-amber-400 align-middle" title="Document required" />
                )}
              </button>
            ))}
          </div>
        </DialogHeader>

        {/* ── Details tab ──────────────────────────────────────────────── */}
        {tab === "details" && (
          <div className="space-y-6 pt-2">

            {/* Contact */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Company Name</Label>
                  {editMode
                    ? <Input value={form.companyName || ""} onChange={e => setForm(p => ({ ...p, companyName: e.target.value }))} data-testid="input-company-name" />
                    : <p className="text-sm">{customer.companyName}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Contact Name</Label>
                  {editMode
                    ? <Input value={form.contactName || ""} onChange={e => setForm(p => ({ ...p, contactName: e.target.value }))} data-testid="input-contact-name" />
                    : <p className="text-sm">{customer.contactName || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  {editMode
                    ? <Input type="email" value={form.emailAddress || ""} onChange={e => setForm(p => ({ ...p, emailAddress: e.target.value }))} data-testid="input-email" />
                    : <p className="text-sm">{customer.emailAddress || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  {editMode
                    ? <Input value={form.phoneNumber || ""} onChange={e => setForm(p => ({ ...p, phoneNumber: e.target.value }))} data-testid="input-phone" />
                    : <p className="text-sm">{customer.phoneNumber || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Website</Label>
                  {editMode
                    ? <Input value={form.website || ""} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://…" data-testid="input-website" />
                    : customer.website
                      ? <a href={/^https?:\/\//i.test(customer.website) ? customer.website : `https://${customer.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" />{customer.website}</a>
                      : <p className="text-sm text-muted-foreground">—</p>}
                </div>
                {editMode && (
                  <div className="flex items-center gap-2 col-span-2">
                    <Checkbox
                      checked={form.emailOptedOut || false}
                      onCheckedChange={v => setForm(p => ({ ...p, emailOptedOut: !!v }))}
                      id="emailOptedOut"
                      data-testid="checkbox-email-opted-out"
                    />
                    <Label htmlFor="emailOptedOut" className="cursor-pointer">Email Opted Out</Label>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Address */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Address
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label>Street Address</Label>
                  {editMode
                    ? <Input value={form.address || ""} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} data-testid="input-address" />
                    : <p className="text-sm">{customer.address || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>City</Label>
                  {editMode
                    ? <Input value={form.city || ""} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} data-testid="input-city" />
                    : <p className="text-sm">{customer.city || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label>State</Label>
                    {editMode
                      ? <Input value={form.state || ""} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} maxLength={2} placeholder="OH" data-testid="input-state" />
                      : <p className="text-sm">{customer.state || <span className="text-muted-foreground">—</span>}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Zip</Label>
                    {editMode
                      ? <Input value={form.zipCode || ""} onChange={e => setForm(p => ({ ...p, zipCode: e.target.value }))} data-testid="input-zip" />
                      : <p className="text-sm">{customer.zipCode || <span className="text-muted-foreground">—</span>}</p>}
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Classification */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Classification</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Business Type</Label>
                  {editMode
                    ? (
                      <Select value={form.businessType || ""} onValueChange={v => setForm(p => ({ ...p, businessType: v }))}>
                        <SelectTrigger data-testid="select-business-type"><SelectValue placeholder="Select type…" /></SelectTrigger>
                        <SelectContent>{businessTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    )
                    : <p className="text-sm">{customer.businessType || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Region</Label>
                  {editMode
                    ? (
                      <Select value={form.region || ""} onValueChange={v => setForm(p => ({ ...p, region: v }))}>
                        <SelectTrigger data-testid="select-region-dialog"><SelectValue placeholder="Select region…" /></SelectTrigger>
                        <SelectContent>{regions.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
                      </Select>
                    )
                    : <p className="text-sm">{customer.region || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Rep</Label>
                  {editMode
                    ? <Input value={form.rep || ""} onChange={e => setForm(p => ({ ...p, rep: e.target.value }))} data-testid="input-rep" />
                    : <p className="text-sm">{customer.rep || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  {editMode
                    ? (
                      <Select value={form.status || "trial"} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                        <SelectTrigger data-testid="select-status-dialog"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.filter((s, i, a) => a.findIndex(x => x.label === s.label) === i).map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )
                    : statusBadge(customer.status)}
                </div>
              </div>
            </div>

            <Separator />

            {/* Subscription pricing */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Subscription Pricing
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Plan Tier</Label>
                  {editMode
                    ? (
                      <Select value={form.planTier || ""} onValueChange={v => setForm(p => ({ ...p, planTier: v }))}>
                        <SelectTrigger data-testid="select-plan-tier"><SelectValue placeholder="Select plan…" /></SelectTrigger>
                        <SelectContent>
                          {["lite", "pro", "enterprise"].map(t => <SelectItem key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )
                    : <p className="text-sm capitalize">{customer.planTier || <span className="text-muted-foreground">—</span>}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Base Rate (cents)</Label>
                  {editMode
                    ? <Input type="number" min={0} value={form.baseRateCents ?? 0} onChange={e => setForm(p => ({ ...p, baseRateCents: parseInt(e.target.value) || 0 }))} data-testid="input-base-rate" />
                    : <p className="text-sm">${((customer.baseRateCents || 0) / 100).toFixed(2)}/mo</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Discount %</Label>
                  {editMode
                    ? <Input type="number" min={0} max={100} step={0.5} value={form.discountPercent ?? "0"} onChange={e => setForm(p => ({ ...p, discountPercent: e.target.value }))} data-testid="input-discount" />
                    : <p className="text-sm">{customer.discountPercent}%</p>}
                </div>
              </div>
              <div className="mt-3 p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Effective MRR</span>
                  <span className="font-semibold text-primary">{mrrDisplay}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Notes */}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              {editMode
                ? <VoiceToTextTextarea value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={5} placeholder="Add notes…" data-testid="textarea-notes" />
                : customer.notes
                  ? <p className="text-sm whitespace-pre-wrap bg-muted/40 rounded-md p-3">{customer.notes}</p>
                  : <p className="text-sm text-muted-foreground italic">No notes yet.</p>}
            </div>

            {/* Requires document flag */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="requiresDocument"
                checked={editMode ? (form.requiresDocument ?? false) : customer.requiresDocument}
                disabled={!editMode}
                onCheckedChange={v => setForm(p => ({ ...p, requiresDocument: !!v }))}
                data-testid="checkbox-requires-document"
              />
              <Label htmlFor="requiresDocument" className={editMode ? "cursor-pointer" : "text-muted-foreground"}>
                Requires document
              </Label>
              {!editMode && customer.requiresDocument && (
                <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Document needed
                </span>
              )}
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              {customer.lastContacted && <p>Last contacted: {new Date(customer.lastContacted).toLocaleDateString()}</p>}
              <p>Customer since: {new Date(customer.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        )}

        {/* ── Activity tab ─────────────────────────────────────────────── */}
        {tab === "activity" && (
          <div className="space-y-4 pt-2">
            {/* Log new */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <h4 className="text-sm font-medium">Log Activity</h4>
              <div className="flex gap-2">
                {(["call", "email", "note"] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setLogType(t)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors capitalize ${
                      logType === t ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80"
                    }`}
                    data-testid={`button-log-type-${t}`}
                  >
                    {t === "call" && <Phone className="w-3 h-3 inline mr-1" />}
                    {t === "email" && <Mail className="w-3 h-3 inline mr-1" />}
                    {t === "note" && <Pencil className="w-3 h-3 inline mr-1" />}
                    {t}
                  </button>
                ))}
              </div>
              <VoiceToTextTextarea
                value={logNotes}
                onChange={e => setLogNotes(e.target.value)}
                placeholder={`Notes for this ${logType}…`}
                rows={3}
                data-testid="textarea-activity-notes"
              />
              <Button
                size="sm"
                onClick={handleLogActivity}
                disabled={!logNotes.trim() || isLogging}
                data-testid="button-log-activity"
              >
                {isLogging && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Send className="w-4 h-4 mr-1" /> Log
              </Button>
            </div>

            {/* Timeline */}
            <div className="space-y-3">
              {activities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No activity yet.</p>
              ) : activities.map(act => (
                <div key={act.id} className="flex gap-3 p-3 border rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    {act.type === "call" && <Phone className="w-4 h-4 text-blue-500" />}
                    {act.type === "email" && <Mail className="w-4 h-4 text-green-500" />}
                    {act.type === "note" && <Pencil className="w-4 h-4 text-orange-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-medium capitalize text-sm">{act.type}</span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(act.createdAt).toLocaleDateString()} {new Date(act.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    {act.notes && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{act.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ── Documents tab ─────────────────────────────────────────────── */}
        {tab === "documents" && (
          <div className="space-y-4 pt-2">

            {/* Requires-document reminder banner */}
            {customer.requiresDocument && documents.filter(d => d.status === "completed").length === 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                A document is required for this customer. Create and send one below.
              </div>
            )}

            {/* New document form */}
            {newDocOpen ? (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <FilePlus className="h-4 w-4" /> New Document
                </h4>

                <div className="space-y-1.5">
                  <Label>Document name</Label>
                  <Input
                    value={newDocName}
                    onChange={e => setNewDocName(e.target.value)}
                    placeholder="e.g. Service Agreement — Acme Corp"
                    data-testid="input-new-doc-name"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Start from template (optional)</Label>
                  <Select value={newDocTemplateId} onValueChange={v => {
                    setNewDocTemplateId(v);
                    if (v !== "none") {
                      const tmpl = docTemplates.find(t => t.id === v);
                      if (tmpl) setNewDocBody(tmpl.bodyHtml);
                    }
                  }}>
                    <SelectTrigger data-testid="select-doc-template">
                      <SelectValue placeholder="Blank document" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Blank document</SelectItem>
                      {docTemplates.filter(t => t.status === "published").map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Document body (HTML or plain text)</Label>
                  <VoiceToTextTextarea
                    value={newDocBody}
                    onChange={e => setNewDocBody(e.target.value)}
                    rows={6}
                    placeholder="Paste or type the document content here…"
                    data-testid="textarea-new-doc-body"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Recipient name</Label>
                    <Input
                      value={newDocRecipientName}
                      onChange={e => setNewDocRecipientName(e.target.value)}
                      placeholder={customer.contactName || ""}
                      data-testid="input-new-doc-recipient-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Recipient email</Label>
                    <Input
                      type="email"
                      value={newDocRecipientEmail}
                      onChange={e => setNewDocRecipientEmail(e.target.value)}
                      placeholder={customer.emailAddress || ""}
                      data-testid="input-new-doc-recipient-email"
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setNewDocOpen(false);
                      setNewDocName(""); setNewDocTemplateId("none");
                      setNewDocBody(""); setNewDocRecipientName(""); setNewDocRecipientEmail("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!newDocName.trim()}
                    data-testid="button-create-doc"
                    onClick={async () => {
                      try {
                        await apiRequest("POST", `/api/platform/customers/${customer.id}/documents`, {
                          displayName: newDocName.trim(),
                          templateId: newDocTemplateId === "none" ? null : newDocTemplateId,
                          bodyHtmlSnapshot: newDocBody,
                          recipientName: newDocRecipientName || customer.contactName || null,
                          recipientEmail: newDocRecipientEmail || customer.emailAddress || null,
                        });
                        refetchDocs();
                        qc.invalidateQueries({ queryKey: ["/api/platform/customers", customer.id, "documents"] });
                        setNewDocOpen(false);
                        setNewDocName(""); setNewDocTemplateId("none");
                        setNewDocBody(""); setNewDocRecipientName(""); setNewDocRecipientEmail("");
                        toast({ title: "Document created" });
                      } catch {
                        toast({ title: "Failed to create document", variant: "destructive" });
                      }
                    }}
                  >
                    Create document
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewDocOpen(true)}
                data-testid="button-new-document"
              >
                <FilePlus className="h-4 w-4 mr-2" /> New document
              </Button>
            )}

            {/* Document list */}
            {documents.length === 0 && !newDocOpen && (
              <div className="py-10 text-center">
                <FileText className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-muted-foreground">No documents yet.</p>
              </div>
            )}

            <div className="space-y-2">
              {documents.map(doc => {
                const statusColor: Record<string, string> = {
                  draft: "bg-gray-100 text-gray-600",
                  sent: "bg-blue-100 text-blue-700",
                  completed: "bg-green-100 text-green-700",
                  declined: "bg-red-100 text-red-700",
                  voided: "bg-gray-100 text-gray-400",
                };
                const signingUrl = doc.signingToken
                  ? `${window.location.origin}/platform-sign/${doc.signingToken}`
                  : null;
                const isSending = sendingDocId === doc.id;

                return (
                  <div key={doc.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">{doc.displayName}</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${statusColor[doc.status] || statusColor.draft}`}>
                            {doc.status}
                          </span>
                        </div>
                        {doc.recipientName && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            For: {doc.recipientName}{doc.recipientEmail ? ` · ${doc.recipientEmail}` : ""}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Created {new Date(doc.createdAt).toLocaleDateString()}
                          {doc.sentAt && ` · Sent ${new Date(doc.sentAt).toLocaleDateString()}`}
                        </p>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {/* Send button (draft or re-send for sent) */}
                        {(doc.status === "draft" || doc.status === "sent") && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isSending}
                            data-testid={`button-send-doc-${doc.id}`}
                            onClick={async () => {
                              setSendingDocId(doc.id);
                              try {
                                const res = await apiRequest(
                                  "POST",
                                  `/api/platform/customers/${customer.id}/documents/${doc.id}/send`,
                                  {}
                                );
                                const data = await res.json();
                                refetchDocs();
                                qc.invalidateQueries({ queryKey: ["/api/platform/customers", customer.id, "documents"] });
                                toast({
                                  title: "Signing link ready",
                                  description: data.signingUrl ? "Copy the link from the document card." : "Document sent.",
                                });
                              } catch {
                                toast({ title: "Failed to send document", variant: "destructive" });
                              } finally {
                                setSendingDocId(null);
                              }
                            }}
                          >
                            {isSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                            <span className="ml-1 text-xs">{doc.status === "draft" ? "Send" : "Resend"}</span>
                          </Button>
                        )}

                        {/* Void / delete */}
                        {doc.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            data-testid={`button-void-doc-${doc.id}`}
                            onClick={async () => {
                              try {
                                await apiRequest(
                                  "DELETE",
                                  `/api/platform/customers/${customer.id}/documents/${doc.id}`,
                                  {}
                                );
                                refetchDocs();
                                qc.invalidateQueries({ queryKey: ["/api/platform/customers", customer.id, "documents"] });
                                toast({ title: doc.status === "draft" ? "Document deleted" : "Document voided" });
                              } catch {
                                toast({ title: "Failed to remove document", variant: "destructive" });
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Signing link copy row */}
                    {signingUrl && doc.status === "sent" && (
                      <div className="flex items-center gap-2 bg-muted/40 rounded px-2 py-1.5">
                        <span className="text-xs text-muted-foreground truncate flex-1 font-mono">
                          {signingUrl}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 shrink-0"
                          data-testid={`button-copy-sign-link-${doc.id}`}
                          onClick={() => {
                            navigator.clipboard.writeText(signingUrl);
                            setCopied(doc.id);
                            setTimeout(() => setCopied(null), 2000);
                          }}
                        >
                          {copied === doc.id ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    )}

                    {/* Completed audit info */}
                    {doc.status === "completed" && (
                      <div className="bg-green-50 border border-green-200 rounded px-2.5 py-2 space-y-0.5">
                        <div className="flex items-center gap-1.5 text-xs text-green-700 font-medium">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          Signed by {doc.signedByName || "—"} ({doc.signedByEmail || "—"})
                        </div>
                        {doc.signedCompletedAt && (
                          <div className="text-xs text-green-600">
                            {new Date(doc.signedCompletedAt).toLocaleString()}
                          </div>
                        )}
                        {doc.signedAuditHash && (
                          <div className="text-[10px] text-green-500 font-mono">
                            Hash: {doc.signedAuditHash.slice(0, 32)}…
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
